import React, { ChangeEvent, memo, SyntheticEvent, useState } from 'react'
import { Control, Controller, UseFormMethods } from 'react-hook-form'
import { ISelectOption, Select } from 'components/forms/Selects'
import { Button } from 'transfix-ui/components/Button'
import { ICarrierApplicationStates } from 'types/graphqlTypes'
import { ICarrierResultFragment } from 'apps/marketplace/components/CarrierAutosuggest/graphql/CarrierResultFragment'
import ControlledCarrierAutosuggest from 'apps/marketplace/components/ControlledCarrierAutosuggest'
import { IRFPLane } from 'apps/marketplace/utils/api/rfpLanes'
import { RFP_RATE_TYPE_OPTIONS } from 'apps/marketplace/utils/constants/rfpRateTypes'
import { RateFormErrors } from 'apps/marketplace/utils/hooks/useRFPLaneRateForm'
import { TextInput } from 'components/atoms'
import { Checkbox } from 'components/forms'
import { ButtonGroup, Card, Spinner } from 'transfix-ui'

interface ICarrierResult {
  id: string
  name?: string
  mcNumber?: string
  dotNumber?: string
}

export enum CapacityType {
  range = 'range',
  single = 'single',
}

export const RateForm = memo(
  ({
    awardedLaneHeader,
    capacityIsRange = false,
    control,
    defaultCarrier,
    formErrors,
    loading,
    selectedRFPLane,
    setValue,
  }: {
    selectedRFPLane?: IRFPLane
    control: Control
    setValue: UseFormMethods['setValue']
    defaultCarrier?: ICarrierResult
    capacityIsRange: boolean
    formErrors?: RateFormErrors
    loading: boolean
    awardedLaneHeader?: React.ReactElement
  }) => {
    const [capacityType, setCapacityRange] = useState<CapacityType>(
      capacityIsRange ? CapacityType.range : CapacityType.single
    )
    const isRange: boolean = capacityType === CapacityType.range

    const carrierInfo = defaultCarrier?.id
      ? {
          id: defaultCarrier.id,
          mcNumber: defaultCarrier?.mcNumber,
          state: ICarrierApplicationStates.other,
          dotNumber: defaultCarrier?.dotNumber,
          name: defaultCarrier?.name,
        }
      : null

    const labelGenerator = (carrier: Partial<ICarrierResultFragment>) => {
      const compliment =
        carrier.mcNumber && carrier.dotNumber
          ? ` | ID: ${carrier.id} | MC: ${carrier.mcNumber} | DOT: ${carrier.dotNumber}`
          : ''
      return `${carrier.name}${compliment}`
    }
    return (
      <>
        {awardedLaneHeader && awardedLaneHeader}
        <div className='pa5'>
          {loading && <Spinner />}
          {!loading && (
            <>
              {!awardedLaneHeader && selectedRFPLane && (
                <>
                  <h3 className='ma0'>
                    {selectedRFPLane.shipperName} | {selectedRFPLane?.pickupCity},{' '}
                    {selectedRFPLane?.pickupStateCode} - {selectedRFPLane?.deliveryCity},{' '}
                    {selectedRFPLane?.deliveryStateCode}
                  </h3>
                  <div className='mb4'>{selectedRFPLane.rfpTitle}</div>
                </>
              )}
              <Card title='Add Rate'>
                <div className='text-secondary'>Input data and add rate.</div>
                <div className='flex mt3'>
                  <div className='w-20'>
                    <Controller
                      name='rateAttributes.amount'
                      control={control}
                      render={({ onChange, value }) => {
                        const updateTextInput = (e: ChangeEvent<HTMLInputElement>) => {
                          onChange(e.target.value)
                        }
                        return (
                          <TextInput
                            onChange={updateTextInput}
                            dataTest='rate-amount'
                            label='Rate Amount'
                            type='number'
                            name='amount'
                            clearable={false}
                            value={value}
                            required
                            validationMessage={formErrors?.rateAttributes?.amount?.message}
                            invalid={!!formErrors?.rateAttributes?.amount?.message}
                          />
                        )
                      }}
                    />
                  </div>
                  <div className='w-25 ml3' data-test='carrier-autosuggest'>
                    <ControlledCarrierAutosuggest
                      name='carrierApplicationId'
                      control={control}
                      defaultValue={defaultCarrier?.id}
                      carrier={carrierInfo}
                      optionLabelGenerator={labelGenerator}
                      AutocompleteProps={{
                        label: 'Carrier',
                        required: true,
                        size: 'small',
                        placeholder: 'Enter carrier name',
                        inputError: Boolean(formErrors?.carrierApplicationId?.message),
                      }}
                    />
                  </div>
                  <div className='w-10 ml3'>
                    <Controller
                      name='rateAttributes.rateTypeKey'
                      control={control}
                      render={({ onChange, value }) => {
                        const onSelectChange = (opt: ISelectOption<string>) => {
                          onChange(opt.value)
                        }
                        return (
                          <Select
                            options={RFP_RATE_TYPE_OPTIONS}
                            label='Rate Type'
                            dataTest='rate-type'
                            value={value}
                            onChange={onSelectChange}
                            required
                            validationMessage={formErrors?.rateAttributes?.rateTypeKey?.message}
                            invalid={!!formErrors?.rateAttributes?.rateTypeKey?.message}
                          />
                        )
                      }}
                    />
                  </div>
                  <div className='flex'>
                    <ButtonGroup
                      className='ml3'
                      value={capacityType}
                      onChange={(value: string) => {
                        setCapacityRange(value as CapacityType)
                      }}
                      dataTest='assign-driver'>
                      <Button
                        value={CapacityType.single}
                        data-test='single-toggle'
                        onClick={() => {
                          setValue('minCapacity', null)
                        }}>
                        Single
                      </Button>
                      <Button value={CapacityType.range} data-test='range-toggle'>
                        Range
                      </Button>
                    </ButtonGroup>
                  </div>
                  <div className={isRange ? 'w-10 ml3' : ''}>
                    <Controller
                      name='minCapacity'
                      control={control}
                      render={({ onChange, value }) => {
                        const updateTextInput = (e: ChangeEvent<HTMLInputElement>) => {
                          onChange(e.target.value)
                        }
                        return (
                          <>
                            {isRange && (
                              <TextInput
                                onChange={updateTextInput}
                                dataTest='min-capacity'
                                label='Min Weekly Capacity'
                                type='number'
                                name='minCapacity'
                                clearable={false}
                                value={value}
                              />
                            )}
                          </>
                        )
                      }}
                    />
                  </div>
                  <div className='w-10 ml3'>
                    <Controller
                      name='maxCapacity'
                      control={control}
                      render={({ onChange, value }) => {
                        const updateTextInput = (e: ChangeEvent<HTMLInputElement>) => {
                          onChange(e.target.value)
                        }
                        return (
                          <TextInput
                            onChange={updateTextInput}
                            dataTest='max-capacity'
                            label={`${isRange ? 'Max ' : ''}Weekly Capacity`}
                            type='number'
                            name='maxCapacity'
                            clearable={false}
                            value={value}
                          />
                        )
                      }}
                    />
                  </div>
                </div>
                <div className='flex mt3'>
                  <Controller
                    name='canDoWeekends'
                    control={control}
                    render={({ onChange, value }) => {
                      const updateCheckbox = (e: SyntheticEvent<HTMLInputElement>) => {
                        onChange(e.currentTarget.checked)
                      }
                      return (
                        <Checkbox
                          id='canDoWeekends'
                          dataTest='can-do-weekends'
                          label='Can do Weekends'
                          checked={value}
                          onChange={updateCheckbox}
                        />
                      )
                    }}
                  />
                </div>
                <div className='flex mt3'>
                  <Controller
                    name='confirmed'
                    control={control}
                    render={({ onChange, value }) => {
                      const updateCheckbox = (e: SyntheticEvent<HTMLInputElement>) => {
                        onChange(e.currentTarget.checked)
                      }
                      return (
                        <Checkbox
                          id='confirmed'
                          dataTest='confirmed'
                          label='Confirmed'
                          checked={value}
                          onChange={updateCheckbox}
                        />
                      )
                    }}
                  />
                </div>
              </Card>
            </>
          )}
        </div>
      </>
    )
  }
)
